import humps from 'humps'
import api from 'api'
import { resolveDc3ThematicArea } from 'config/dc3ThematicAreas'
import {
  IDc3AttestationTemplateData,
  IDc3CertificateData,
  IDc3CertificateDownloadResponse,
  IDc3ExecutionPeriod,
} from 'components/dc3Certificate/types'

export interface GetDc3CertificateDownloadDataParams {
  courseId: number
  userIds?: number[]
}

const mapAttestation = (
  attestation: IDc3CertificateDownloadResponse['attestation']
): IDc3AttestationTemplateData => ({
  id: attestation.id,
  companyFiscalName: attestation.companyFiscalName,
  companyRfc: attestation.companyRfc,
  patronalRegistration: attestation.patronalRegistration,
  companyActivity: attestation.companyActivity,
  trainingAgentName: attestation.trainingAgentName,
  trainingAgentStpsRegistration: attestation.trainingAgentStpsRegistration,
  thematicArea: resolveDc3ThematicArea(attestation.thematicArea) ?? {
    code: '',
    name: '',
  },
  instructorName: attestation.instructorName,
  instructorSignatureUrl: attestation.instructorSignatureUrl,
  legalRepresentativeName: attestation.legalRepresentativeName,
  legalRepresentativeSignatureUrl: attestation.legalRepresentativeSignatureUrl,
  workersRepresentativeName: attestation.workersRepresentativeName,
  workersRepresentativeSignatureUrl:
    attestation.workersRepresentativeSignatureUrl,
})

const mapExecutionPeriod = (
  executionPeriod: IDc3ExecutionPeriod
): IDc3ExecutionPeriod => ({
  startDate: executionPeriod.startDate,
  finishDate: executionPeriod.finishDate,
})

export const mapDownloadResponseToCertificates = (
  response: IDc3CertificateDownloadResponse
): IDc3CertificateData[] => {
  const attestation = mapAttestation(response.attestation)

  return response.participants.map((participant) => ({
    attestation,
    course: {
      name: response.course.name,
      hours: response.course.hours,
    },
    worker: {
      fullName: participant.fullName,
      curp: participant.curp || '',
      occupation: participant.occupation || '',
      position: participant.position || '',
    },
    executionPeriod: mapExecutionPeriod(participant.executionPeriod),
  }))
}

const getDc3CertificateDownloadData = async ({
  courseId,
  userIds,
}: GetDc3CertificateDownloadDataParams) => {
  const { data } = await api.post<IDc3CertificateDownloadResponse>(
    `/administration/courses/${courseId}/dc3_attestation_download_data`,
    { user_ids: userIds }
  )

  return humps.camelizeKeys(data) as IDc3CertificateDownloadResponse
}

export default getDc3CertificateDownloadData
